import { useCallback, useEffect, useState } from 'react';

export const useStringQueryParam = (
  param: string,
  defaultValue: string = ''
): [string, (value: string) => void] => {
  const isBrowser = typeof window !== 'undefined';

  const getValue = useCallback(() => {
    if (!isBrowser) return defaultValue;
    const params = new URLSearchParams(window.location.search);
    return params.get(param) || defaultValue;
  }, [param, defaultValue, isBrowser]);

  const [value, setValueState] = useState<string>(getValue);

  useEffect(() => {
    if (isBrowser) {
      setValueState(getValue());
    }
  }, [isBrowser, getValue]);

  const setValue = useCallback(
    (newValue: string) => {
      if (!isBrowser) return;

      const params = new URLSearchParams(window.location.search);

      if (newValue === defaultValue || newValue === '') {
        params.delete(param);
      } else {
        params.set(param, newValue);
      }

      const newSearch = params.toString();
      const newUrl = `${window.location.pathname}${
        newSearch ? `?${newSearch}` : ''
      }${window.location.hash}`;

      window.history.pushState({}, '', newUrl);
      setValueState(newValue);
    },
    [param, defaultValue, isBrowser]
  );

  // Listen for browser back/forward navigation
  useEffect(() => {
    if (!isBrowser) return;

    const handlePopState = () => {
      setValueState(getValue());
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isBrowser, getValue]);

  return [value, setValue];
};
